<template>
  <ContentPage back>
    <template #header>
      <content-page-title>
        Auto Refill
      </content-page-title>
    </template>

    <Section>

      <v-form @submit.prevent="onSubmit" ref="form">
        <div class="d-flex align-center">
          <div class="d-flex text--center">Auto Refill</div>
          <v-switch
            v-model="enabled"
            label="On"
            @click="checkToggle"
            class="ml-auto my-0" hide-details
          />
        </div>
        
        <div style="line-height: 1.2" class="mt-4">
          Never miss a lead. Set your account to automatically refill your
          balance when it runs low.
        </div>

        <div class="d-flex">
          <div style="max-width: 40%">
            <div :class="label_classes">
              Automatically add
            </div>
            <v-text-field
              dense outlined validate-on-blur hide-details="auto" prefix="$"
              v-model="deposit"
              :rules="[required, gt_floor, lte_200]"
              :disabled="!enabled"
            />
          </div>

          <div class="ml-3">
            <div :class="label_classes">
              When my balance is below
            </div>
            <div class="d-flex align-center">
              <v-text-field
                dense outlined validate_on_blur
                hide-details="auto" prefix="$"
                placeholder="12.00"
                v-model="floor"
                :rules="[required, gte_12]"
                style="max-width: 130px;"
                :disabled="!enabled"
              />
            </div>  
          </div>
        </div>

        <div class="grey--text font-italic ml-3 mt-5" style="line-height: 1.2; font-size: 12px;">
          A non-refundible 2.9% + $0.30 service fee will be included.
        </div>

        <v-divider class="mt-4" />

        <div class="d-flex justify-center pt-4 pb-2">
          <v-btn v-if="enabled" @click="close">Cancel</v-btn>
          <v-btn class="ml-3" v-if="enabled" type="submit" color="primary" :disabled="!enabled">
            Confirm
          </v-btn>
          <v-btn v-if="!enabled" color="primary" @click="close">Back</v-btn>
        </div>
      </v-form>
    </Section>

    <ErrorOverlay
      title="Error" v-model="error_dialog"
      :loading="loading" :message="error_message"
    />
  </ContentPage>
</template>

<script>
import { ContentPage, ContentPageTitle, ErrorOverlay, Section } from '@/components/pages'

import { mapActions, mapGetters } from 'vuex'
import formatters from '@/lib/formatters'
import { required } from '@/lib/validations'

export default {
  name: 'AutoRefillView',

  components: { ContentPage, ContentPageTitle, ErrorOverlay, Section },

  data() {
    return {
      enabled: true,
      floor: 12,
      deposit: 30,

      label_classes: [ 'mt-3', 'mb-2', 'black--text' ],

      // validation rules
      gte_12: v => Number(v) >= 12 || '$12 minimum',
      lte_200: v => Number(v) <= 200 || '$200 max',
      required,

      // error handling
      loading: false,
      error_dialog: false,
      error_message: '',
    }
  },

  computed: {
    ...mapGetters('billing', ['refillPolicy']),
  },

  watch: {
    refillPolicy(newPolicy) {
      if(newPolicy.refill_policy_id) {
        this.enabled = newPolicy.enabled
        this.deposit = newPolicy.deposit_cents / 100.0
        this.floor = newPolicy.floor_cents / 100.0
      } else {
        this.enabled = true
        this.deposit = 30
        this.floor = 12
      }
    },
  },

  methods: {
    ...mapActions('payment_methods', ['loadPaymentMethods']),
    ...mapActions('billing', ['loadRefillPolicy', 'updateRefillPolicy']),
    ...formatters,

    checkToggle() {
      // if they previously had a policy, just disable it
      if (!this.enabled && this.refillPolicy.refill_policy_id) {
        this.toggleOff()
      }
    },

    close() {
      this.$router.replace({ name: 'billing/account' })
    },

    gt_floor(v) {
      return Number(v) >= Number(this.floor) || "Can't be less than the balance minimum"
    },

    async toggleOff() {
      const payload = { enabled: false }

      this.loading = true
      const result = await this.updateRefillPolicy(payload)
      this.loading = false

      if (!result.ok) {
        this.error_dialog = true
        this.error_message = "An error occurred!"
      }
    },

    async onSubmit() {
      if (this.$refs.form.validate()) {

        const payload = {
          enabled: true,
          deposit_cents: (this.deposit * 100).toFixed(),
          floor_cents: (this.floor * 100).toFixed(),
        }
        
        this.loading = true
        const result = await this.updateRefillPolicy(payload)
        this.loading = false

        if (result.ok) {
          this.close()
        } else {
          this.error_dialog = true
          this.error_message = "An error occurred setting up your automatic refill."
        }
      }
    },
  },

  async created() {
    this.loading = true

    let p = this.loadRefillPolicy()
    await this.loadPaymentMethods()

    await p

    this.loading = false
  }
}
</script>